import { useEffect, useRef, useState, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router";

import { postForm, unavailableMessage } from "../../api/client.js";
import { queryValue } from "../../app/navigation.js";
import { forgetReturnAfterRegister, returnAfterRegister, withReturnUrl } from "../../app/return-after-register.js";
import { useGo } from "../../app/use-go.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { MessagePage } from "../../components/message-page.js";
import { FormSummary } from "../../forms/fields.js";

import { accountApi, accountUrls } from "./urls.js";

/**
 * 確認メールのリンク（<c>/Account/ConfirmEmail?userId=&amp;code=</c>）。
 *
 * <b>開いたら API へ送り、返った行き先（有効化できた／できなかった）へ移る。</b>
 * 確認は書き込みなので GET の API にはしない（CSRF の検証を通す）。
 * <b>送るのは 1 度だけ</b>——StrictMode は開発中に副作用を 2 度走らせる。
 */
export function ConfirmEmailPage(): ReactElement {
    usePageMeta("アカウント登録の確認");
    const go = useGo();
    const [search] = useSearchParams();
    const userId = queryValue(search, "userId") ?? "";
    const code = queryValue(search, "code") ?? "";
    const sent = useRef(false);
    const [problem, setProblem] = useState<string>();

    useEffect(() => {
        if (sent.current) {
            return;
        }
        sent.current = true;

        void postForm(accountApi.confirmEmail, new URLSearchParams({ userId, code }))
            .then((outcome) => {
                if (outcome.kind === "go") {
                    go(outcome.location);
                } else {
                    // 叩きすぎ・相手の都合。リンクが悪いとは限らないので、失敗の画面へは送らない
                    setProblem(outcome.kind === "errors"
                        ? Object.values(outcome.errors).flat().join(" ")
                        : unavailableMessage);
                }
            })
            .catch(() => {
                setProblem(unavailableMessage);
            });
    }, [go, userId, code]);

    return (
        <div className="page max-w-lg">
            {problem === undefined
                ? <p className="text-slate-500">確認しています…</p>
                : <FormSummary messages={[problem]} />}
        </div>
    );
}

/**
 * 登録が済んだ。<b>登録の画面で控えた戻り先（料金プランで選んだ内容の申し込み）があれば、ログインの戻り先に付ける</b>
 * （<c>app/return-after-register.ts</c>）。押したら控えを消す。
 */
export function ConfirmEmailSuccessPage(): ReactElement {
    const [returnUrl] = useState(() => returnAfterRegister());

    return (
        <MessagePage
            title="アカウント登録が完了しました"
            actions={
                <Link to={withReturnUrl(accountUrls.login, returnUrl)} className="btn-primary" onClick={forgetReturnAfterRegister}>
                    ログイン画面へ
                </Link>
            }
        >
            ご登録ありがとうございます。<br />ログインしてご利用を開始してください。
        </MessagePage>
    );
}

/**
 * 有効化できなかった。<b>パスワード再設定のリンクにコードが無いときもこれを出す</b>。
 * 題名だけ見出しより短い。
 */
export function ConfirmEmailFailedPage(): ReactElement {
    usePageMeta("登録を完了できませんでした");

    return (
        <div className="page max-w-lg">
            <div className="panel-message">
                <h1 className="page-title">アカウント登録を完了できませんでした</h1>
                {/* 24 時間は Implem.Portal.Backend のトークンの既定（DataProtectorTokenProvider の TokenLifespan）と同じ */}
                {/* 確認前のアドレスで登録し直すと、Backend は新しいトークンで確認メールを送り直す。2 回目のパスワードは使わない（docs/features/01 §1） */}
                {/* 確認前の人はパスワードの再設定も使えないので、はじめのパスワードを忘れたときだけお問い合わせへ */}
                <p className="page-lead">
                    リンクの有効期限が切れているか、すでに使われている可能性があります。
                    登録がすでに完了している場合は、そのままログインできます。
                    <br />
                    リンクの有効期限（24 時間）が切れた場合は、同じメールアドレスでもう一度アカウント登録すると、新しい確認メールが届きます。
                    パスワードは、はじめに登録したものがそのまま使われます。
                    はじめに登録したパスワードを忘れた場合は、お問い合わせからご連絡ください。
                </p>
                {/* 操作の入口は文中のリンクにしない（docs/01 §6）。主の道は登録し直し */}
                <div className="flex flex-col justify-center gap-3 sm:flex-row">
                    <Link to={accountUrls.register} className="btn-primary">アカウント登録へ戻る</Link>
                    <a href="/Home/Contact" className="btn-secondary">お問い合わせ</a>
                </div>
            </div>
        </div>
    );
}
